import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  List,
  ListItem,
  Paper,
  Fade,
  Grid,
  Tooltip,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import { workoutAnalyzerService, RecognizeWorkoutResponse } from '../../services/workout-analyzer.service';
import { toastService } from '../../services/toast.service';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';

interface QuickWorkoutAddModalProps {
  open: boolean;
  onClose: () => void;
  selectedDate: string; // YYYY-MM-DD
  onRefreshDashboard?: () => void;
}

export const QuickWorkoutAddModal: React.FC<QuickWorkoutAddModalProps> = ({ 
  open, 
  onClose, 
  selectedDate,
  onRefreshDashboard 
}) => {
  const [inputText, setInputText] = useState('');
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [recognizedEvents, setRecognizedEvents] = useState<RecognizeWorkoutResponse[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleClose = () => {
    setInputText('');
    setRecognizedEvents([]);
    setIsRecognizing(false);
    setEditingIndex(null);
    onClose();
  };

  const handleRecognize = async () => {
    if (!inputText.trim()) return;

    setIsRecognizing(true);
    setEditingIndex(null);
    try {
      const now = new Date();
      const [year, month, day] = selectedDate.split('-').map(Number);
      const contextDate = new Date(year, month - 1, day, now.getHours(), now.getMinutes(), now.getSeconds());

      const data = await workoutAnalyzerService.recognize({
        text: inputText,
        contextDate: contextDate.toISOString()
      });
      setRecognizedEvents(data.items);
    } catch (error: any) {
      console.error('Workout recognition error:', error);
      toastService.error(error?.message || 'Не вдалося розпізнати тренування. Спробуйте ще раз.');
    } finally {
      setIsRecognizing(false);
    }
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'strength': return <FitnessCenterIcon sx={{ color: '#FF9800' }} />;
      case 'cardio': return <DirectionsRunIcon sx={{ color: '#2196F3' }} />;
      case 'stretching': return <SelfImprovementIcon sx={{ color: '#9C27B0' }} />;
      default: return <QuestionMarkIcon sx={{ color: '#A8A8A8' }} />;
    }
  };

  const formatTime = (isoString: string) => {
    try {
      return new Date(isoString).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return '';
    }
  };

  const handleUpdateEventField = (index: number, field: keyof RecognizeWorkoutResponse, value: any) => {
    const updatedEvents = [...recognizedEvents];
    updatedEvents[index] = { ...updatedEvents[index], [field]: value };
    setRecognizedEvents(updatedEvents);
  };

  const handleConfirmAll = async () => {
    toastService.success('Тренування успішно додано (імітація)');
    if (onRefreshDashboard) onRefreshDashboard();
    handleClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '500px',
          borderRadius: '16px',
        }
      }}
      data-i-doc={ModalIndices.QUICK_WORKOUT_ADD}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoFixHighIcon sx={{ color: '#FF9800' }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Швидке тренування через ШІ</Typography>
        </Box>
        <IconButton aria-label="close" onClick={handleClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f9f9f9', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <TextField
            fullWidth
            label="Опишіть ваше тренування..."
            placeholder="Наприклад: 10 віджимань, 15 присідань, розминка 5 хв"
            variant="outlined"
            multiline
            rows={recognizedEvents.length > 0 ? 3 : 6}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            disabled={isRecognizing}
            sx={{
              bgcolor: 'white',
              transition: 'all 0.3s ease',
              '& .MuiInputBase-root': {
                borderRadius: '12px',
              }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button 
              onClick={handleRecognize} 
              variant="contained" 
              disabled={isRecognizing || !inputText.trim()}
              startIcon={isRecognizing ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
              sx={{ 
                backgroundColor: '#FF9800', 
                borderRadius: '10px',
                px: 3,
                '&:hover': { backgroundColor: '#e68a00' } 
              }}
            >
              {isRecognizing ? 'Розпізнаємо...' : (recognizedEvents.length > 0 ? 'Розпізнати заново' : 'Розпізнати')}
            </Button>
          </Box>
        </Box>

        {recognizedEvents.length > 0 && (
          <Fade in={recognizedEvents.length > 0}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#282828' }}>
                Розпізнані вправи:
              </Typography>
              <List disablePadding>
                {recognizedEvents.map((event, index) => (
                  <Paper 
                    key={index} 
                    variant="outlined" 
                    sx={{ 
                        mb: 1.5, 
                        borderRadius: '12px', 
                        overflow: 'hidden',
                        borderColor: editingIndex === index ? '#FF9800' : '#e0e0e0',
                        borderWidth: editingIndex === index ? 2 : 1
                    }}
                  >
                    <ListItem sx={{ py: 1.5, flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                          {getEventIcon(event.type)}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          {editingIndex === index ? (
                            <TextField 
                                fullWidth
                                size="small"
                                label="Назва вправи"
                                value={event.name}
                                onChange={(e) => handleUpdateEventField(index, 'name', e.target.value)}
                            />
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                    {event.name}
                                </Typography>
                                {event.originalText && (
                                    <Tooltip title={`Оригінальний текст: "${event.originalText}"`}>
                                        <InfoIcon sx={{ fontSize: '1rem', color: '#A8A8A8', cursor: 'help' }} />
                                    </Tooltip>
                                )}
                                <Tooltip title={`Час: ${formatTime(event.scheduledLdt)}`}>
                                    <AccessTimeIcon sx={{ fontSize: '1rem', color: '#A8A8A8', cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                          )}
                        </Box>
                        <Button 
                          size="small" 
                          variant={editingIndex === index ? "contained" : "text"} 
                          color={editingIndex === index ? "warning" : "inherit"}
                          onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                          startIcon={editingIndex === index ? <SaveIcon /> : <EditIcon />}
                          sx={{ ml: 1 }}
                        >
                          {editingIndex === index ? 'Зберегти' : 'Змінити'}
                        </Button>
                      </Box>

                      <Grid container spacing={2} sx={{ width: '100%', ml: 4 }}>
                        <Grid item xs={6} sm={3}>
                          {editingIndex === index ? (
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Тип"
                              value={event.type}
                              onChange={(e) => handleUpdateEventField(index, 'type', e.target.value)}
                            >
                              <MenuItem value="strength">Силове</MenuItem>
                              <MenuItem value="cardio">Кардіо</MenuItem>
                              <MenuItem value="stretching">Розтяжка</MenuItem>
                              <MenuItem value="unrecognized">Невідомо</MenuItem>
                            </TextField>
                          ) : (
                            <Typography variant="body2"><b>Тип:</b> {event.type}</Typography>
                          )}
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          {editingIndex === index ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Кількість"
                              type="number"
                              value={event.totalAmount || ''}
                              onChange={(e) => handleUpdateEventField(index, 'totalAmount', e.target.value)}
                            />
                          ) : (
                            <Typography variant="body2"><b>К-сть:</b> {event.totalAmount || 0}</Typography>
                          )}
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          {editingIndex === index ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Од. виміру"
                              value={event.unit || ''}
                              onChange={(e) => handleUpdateEventField(index, 'unit', e.target.value)}
                            />
                          ) : (
                            <Typography variant="body2"><b>Од.:</b> {event.unit || '-'}</Typography>
                          )}
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          {editingIndex === index ? (
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Інтенсивність"
                              value={event.intensity || 'medium'}
                              onChange={(e) => handleUpdateEventField(index, 'intensity', e.target.value)}
                            >
                              <MenuItem value="low">Низька</MenuItem>
                              <MenuItem value="medium">Середня</MenuItem>
                              <MenuItem value="high">Висока</MenuItem>
                            </TextField>
                          ) : (
                            <Typography variant="body2"><b>Інтенс.:</b> {event.intensity || 'medium'}</Typography>
                          )}
                        </Grid>
                      </Grid>
                    </ListItem>
                  </Paper>
                ))}
              </List>
            </Box>
          </Fade>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#f9f9f9' }}>
        <Button onClick={handleClose} color="inherit">
          Скасувати
        </Button>
        <Button 
          onClick={handleConfirmAll}
          variant="contained" 
          disabled={recognizedEvents.length === 0 || isRecognizing || editingIndex !== null}
          sx={{ 
            backgroundColor: '#FF9800', 
            borderRadius: '10px',
            px: 4,
            '&:hover': { backgroundColor: '#e68a00' } 
          }}
        >
          Підтвердити та зберегти
        </Button>
      </DialogActions>
    </Dialog>
  );
};
